/** Max ornament upload size (2 MB). */
export const MAX_ORNAMENT_FILE_BYTES = 2 * 1024 * 1024;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const loadImageFromFile = (file) => {
  const url = URL.createObjectURL(file);
  return loadImage(url).finally(() => URL.revokeObjectURL(url));
};

const drawToCanvas = (img, maxWidth, maxHeight) => {
  let { width, height } = img;
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  width = Math.max(1, Math.round(width * scale));
  height = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
};

const encodeUnderLimit = (canvas, maxBytes) => {
  let quality = 0.85;
  let dataUrl = canvas.toDataURL("image/jpeg", quality);

  while (dataUrl.length > maxBytes && quality > 0.35) {
    quality -= 0.08;
    dataUrl = canvas.toDataURL("image/jpeg", quality);
  }

  if (dataUrl.length <= maxBytes) return dataUrl;

  let w = canvas.width;
  let h = canvas.height;
  const ctx = canvas.getContext("2d");
  const scratch = document.createElement("canvas");
  const sctx = scratch.getContext("2d");

  while (dataUrl.length > maxBytes && w > 320 && h > 320) {
    w = Math.round(w * 0.85);
    h = Math.round(h * 0.85);
    scratch.width = w;
    scratch.height = h;
    sctx.drawImage(canvas, 0, 0, w, h);
    quality = 0.75;
    dataUrl = scratch.toDataURL("image/jpeg", quality);
    while (dataUrl.length > maxBytes && quality > 0.35) {
      quality -= 0.08;
      dataUrl = scratch.toDataURL("image/jpeg", quality);
    }
  }

  return dataUrl;
};

/**
 * Compress an image file to a JPEG data URL that fits Firestore document limits.
 */
export async function compressImageFile(
  file,
  { maxWidth = 1600, maxHeight = 1600, maxBytes = 450000 } = {}
) {
  const img = await loadImageFromFile(file);
  const canvas = drawToCanvas(img, maxWidth, maxHeight);
  return encodeUnderLimit(canvas, maxBytes);
}

/**
 * Compress an existing data URL (e.g. profile picture copied from customer record).
 */
export async function compressDataUrl(
  dataUrl,
  { maxWidth = 320, maxHeight = 320, maxBytes = 90000 } = {}
) {
  if (!dataUrl || typeof dataUrl !== "string") return dataUrl;
  if (dataUrl.length <= maxBytes) return dataUrl;

  const img = await loadImage(dataUrl);
  const canvas = drawToCanvas(img, maxWidth, maxHeight);
  return encodeUnderLimit(canvas, maxBytes);
}
